import { c as _c } from "react/compiler-runtime";
import { faChevronDown, faChevronUp, faClock, faCube, faMapMarker, faStopwatch, faTags, faUser } from '@fortawesome/pro-regular-svg-icons';
import { faInfo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import humanizeDuration from 'humanize-duration';
import { DateTime } from 'luxon';
import queryString from 'query-string';
import IntervalTree from 'rb-interval-tree';
import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Link as ReachLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import seedrandom from 'seedrandom';
import { Avatar } from 'shared/avatar';
import Button from 'shared/button';
import CalendarPicker from 'shared/calendar-picker';
import DropdownSelect from 'shared/dropdown-select';
import EmptyState from 'shared/empty-state';
import { Box, Column, Flex, Grid, Row } from 'shared/grid';
import { useCurrentDateTime } from 'shared/hooks';
import IconWrapper from 'shared/icon-wrapper';
import Loader from 'shared/loader';
import Modal, { CloseButton } from 'shared/modal';
import { fetchService } from 'shared/redux/services/actions';
import timezones from 'shared/timezones';
import Tippy from 'shared/tooltip';
import { CroppedText, Heading, Link, Text } from 'shared/typography';
import { toBase16 } from 'shared/utils/b58';
import { buildBookableDropdowns, groupSubstitute, ScheduleItem } from 'shared/utils/booking-tools';
import { EMPTY_ARRAY, EMPTY_OBJECT } from 'shared/utils/constants';
import { currencyFormat } from 'shared/utils/currency';
import { getCdnImageUrlFromId } from 'shared/utils/images';
import { buildBookableOptionsList, rulesToScheduleItems } from 'shared/utils/schedule';
import * as shuffleSeed from 'shared/utils/shuffle-seed';
import { getTranslation } from 'shared/utils/text';
import { BookableAvailability, ExternalBusy, getBookableAvailability, Schedule } from 'shared/utils/time';
import { isUUID } from 'shared/utils/uuid';
import { useBookableIds, useDuration, useLocationId } from '../../hooks';
import { useAppDispatch, useAppSelector } from '../../store';
import LocationSelect from '../location-selector';
import { BookStepHeading, BusinessOfflineState } from '../shared';
const messages = defineMessages({
  'No preference': {
    id: 'No preference',
    defaultMessage: 'No preference',
    description: ''
  },
  timeConjunction: {
    id: 'timeConjunction',
    defaultMessage: 'and',
    description: ''
  }
});
function getAssociatedBookableIds(locationId: string, serviceId: string = null, serviceData: Record<string, any>, bookableData: Record<string, any>, bookableGroupData: Record<string, any>) {
  if (locationId && serviceId && serviceData[serviceId]) {
    const serviceLocation = serviceData[serviceId].locations.find(sl => sl.location_id === locationId);
    const requirements = groupSubstitute(serviceLocation.requirements, locationId, bookableData, bookableGroupData);
    const ids = buildBookableOptionsList(requirements, bookableData).reduce((agg, cur) => {
      return agg.concat(cur);
    }, []);
    return [...new Set(ids)];
  } else {
    return Object.values<any>(bookableData).filter(b => b.location_ids.includes(locationId)).filter(b => b.is_enabled).map(b => b.id);
  }
}
function slotStatus(bookableAvailability, optionsList, requiredBookableIds = []) {
  // TODO: make sure requirements are satisfyable here!!!!!!!!

  // Sometimes bookables from other branches will be in the requirements
  // but not in the bookableAvailability list
  // let ba = bookableAvailability;

  if (!optionsList || optionsList.length === 0) {
    throw new Error('optionsList not specified');
  }
  const BUSY = 0;
  const PRIVATE = 1;
  const AVAILABLE = 2;
  const usedBookableIds = new Set();
  const bookableIds = [...new Set([].concat.apply([], optionsList))]; // eslint-disable-line prefer-spread

  const bookableAv = bookableIds.reduce((obj, bid: string) => {
    const av = bookableAvailability[bid] || {};
    if (av.busy) {
      obj[bid] = BUSY;
    } else if (av.available) {
      obj[bid] = AVAILABLE;
    } else {
      obj[bid] = PRIVATE;
    }
    return obj;
  }, {});
  const optionStatuses = optionsList.map((bookableIds, i) => {
    const requiredBookableId = requiredBookableIds[i];
    const options = bookableIds.filter(bid => !usedBookableIds.has(bid));
    let maxStatus = 0;
    let usedId = 0;
    if (requiredBookableId !== '*') {
      maxStatus = bookableAv[requiredBookableId] || 0;
      usedId = requiredBookableId;
    } else {
      const statusList = options.map(bid => bookableAv[bid]);
      maxStatus = Math.max(...statusList);
      usedId = options[statusList.indexOf(maxStatus)];
    }
    usedBookableIds.add(usedId);
    return maxStatus;
  });
  const minStatus = Math.min(...optionStatuses);
  return ['busy', 'private', 'available'][minStatus];
}
interface DayPage {
  date: DateTime;
  slots: any[];
}
function getDayPages(startUTC: DateTime, endUTC: DateTime, timezone: string): DayPage[] {
  const start = startUTC.setZone(timezone).startOf('day');
  const end = endUTC.setZone(timezone).startOf('day');
  const calendarDays: DayPage[] = [];
  for (let day = start; day < end; day = day.plus({
    days: 1
  })) {
    calendarDays.push({
      date: day,
      slots: []
    });
  }
  return calendarDays;
}
interface PageSlot extends DayPage {
  slots: (SlotType & {
    locationId: string;
    bookableIds: string[];
  })[];
}
function slotsToPages(bookableSlots: SlotType[], startUTC: DateTime, endUTC: DateTime, timezone: string, locationId: string, bookableIds: string[]): PageSlot[] {
  const pages: PageSlot[] = getDayPages(startUTC, endUTC, timezone);
  for (const slot of bookableSlots) {
    if (slot.status !== 'available') {
      continue;
    }
    const event = {
      ...slot,
      locationId,
      bookableIds
    };

    // This this to avoid timezone weirdness
    for (const page of pages) {
      if (page.date.hasSame(DateTime.fromISO(event.start).setZone(timezone), 'day')) {
        page.slots.push(event);
        break;
      }
    }
  }
  return pages;
}
interface SlotType {
  status: string;
  start: string;
  end: string;
  availability: BookableAvailability;
  reservable: boolean;
}
const getSlotsPublic = ({
  itree = null,
  startUTC = null,
  endUTC = null,
  duration = null,
  bookingWindow = null,
  requiredResources = [],
  bookableOptions = null,
  serviceId,
  bufferBefore = 0,
  bufferAfter = 0,
  slotInterval = 0,
  startTimeScheduling = false,
  locationTimezone
}: {
  itree?: any;
  startUTC?: DateTime;
  endUTC?: DateTime;
  duration?: number;
  bookingWindow?: DateTime[];
  requiredResources?: string[];
  bookableOptions?: any;
  serviceId: string;
  bufferBefore: number;
  bufferAfter: number;
  slotInterval: number;
  startTimeScheduling: boolean;
  locationTimezone: string;
}): SlotType[] => {
  if (!bookableOptions) {
    throw new Error('bookableOptions needs to be specified');
  }
  if (requiredResources.length !== bookableOptions.length) {
    throw new Error('requiredResources needs to be the same length as bookableOptions');
  }
  const slots: SlotType[] = [];
  const searchStart = startUTC;
  const searchEnd = endUTC;
  const searchInterval = 5;
  const postSlotSkipAhead = slotInterval ? slotInterval : duration + bufferAfter;
  let candidateStart;
  let candidateEnd;
  for (candidateStart = searchStart, candidateEnd = candidateStart.plus({
    minutes: duration
  }); startTimeScheduling ? candidateStart < searchEnd : candidateEnd < searchEnd; candidateEnd = candidateStart.plus({
    minutes: duration
  })) {
    // const lastSlotEnd = slots[slots.length - 1]?.end;
    // const notFirstSlotOfDay =
    //   lastSlotEnd &&
    //   DateTime.fromISO(lastSlotEnd)
    //     .setZone(locationTimezone)
    //     .hasSame(
    //       DateTime.fromISO(lastSlotEnd).setZone(locationTimezone),
    //       'day'
    //     );

    // const postSlotSkipAhead =
    //   (slotInterval || duration) + (notFirstSlotOfDay ? bufferAfter : 0);
    const inWindow = bookingWindow[0] < candidateStart && candidateEnd < bookingWindow[1];
    if (!inWindow) {
      candidateStart = candidateStart.plus({
        minutes: searchInterval
      });
      continue;
    }
    const matches = itree.search(candidateStart.toISO().substring(0, 19) + 'Z', candidateEnd.toISO().substring(0, 19) + 'Z');
    const availability = getBookableAvailability(candidateStart.toISO().substring(0, 19) + 'Z', candidateEnd.toISO().substring(0, 19) + 'Z', matches, bookingWindow.map(v => v.toISO().substring(0, 19) + 'Z'), serviceId);
    const status = slotStatus(availability, bookableOptions, requiredResources);
    if (status !== 'available') {
      if (startTimeScheduling && status !== 'busy') {
        // Check if the slot starts in available time
        const availability = getBookableAvailability(candidateStart.toISO().substring(0, 19) + 'Z', candidateStart.plus({
          minutes: slotInterval
        }).toISO().substring(0, 19) + 'Z', matches, bookingWindow.map(v => v.toISO().substring(0, 19) + 'Z'), serviceId);
        const status = slotStatus(availability, bookableOptions, requiredResources);
        if (status !== 'available') {
          candidateStart = candidateStart.plus({
            minutes: searchInterval
          });
          continue;
        }
      } else {
        candidateStart = candidateStart.plus({
          minutes: searchInterval
        });
        continue;
      }
    }

    // Check that buffer times are not busy
    if (bufferBefore || bufferAfter) {
      const bStart = candidateStart.minus({
        minutes: bufferBefore
      });
      const bEnd = candidateStart.plus({
        minutes: bufferAfter + duration
      });
      const matches = itree.search(bStart.toISO().substring(0, 19) + 'Z', bEnd.toISO().substring(0, 19) + 'Z');
      let isBusy = false;
      for (const [start, end] of [[bStart, candidateStart], [candidateEnd, bEnd]]) {
        const availability = getBookableAvailability(start.toISO().substring(0, 19) + 'Z', end.toISO().substring(0, 19) + 'Z', matches, bookingWindow.map(v => v.toISO().substring(0, 19) + 'Z'), serviceId);
        const status = slotStatus(availability, bookableOptions, requiredResources);
        if (status === 'busy') {
          // const localTime = candidateStart.setZone(locationTimezone);
          // if (
          //   localTime.day === 18 &&
          //   localTime.hour > 13
          //   // &&
          //   // localTime.minute === 0
          // ) {
          //   debugger;
          // }
          isBusy = true;
          break;
        }
      }
      if (isBusy) {
        candidateStart = candidateStart.plus({
          minutes: searchInterval
        });
        continue;
      }
    }
    slots.push({
      status: status === 'private' && startTimeScheduling ? 'available' : status,
      start: candidateStart.toISO().substring(0, 19) + 'Z',
      end: candidateEnd.toISO().substring(0, 19) + 'Z',
      availability,
      reservable: false
    });
    candidateStart = candidateStart.plus({
      minutes: postSlotSkipAhead
    });
  }
  return slots;
};
function getValidSelection(locationId, serviceId, bookableIds = EMPTY_ARRAY, wildcardBookableOption = false, serviceData, bookableData, bookableGroupData) {
  // TODO: ensure no duplicate bookable selections

  const serviceLocation = serviceData[serviceId].locations.find(sl => sl.location_id === locationId);
  const requirements = groupSubstitute(serviceLocation.requirements, locationId, bookableData, bookableGroupData);
  let bookableOptions = buildBookableOptionsList(requirements, bookableData);
  const bookableOptionLists = buildBookableDropdowns(requirements, wildcardBookableOption, bookableData);
  if (wildcardBookableOption) {
    bookableOptions = bookableOptions.map((list, idx) => {
      if (list.length > 1) {
        if (bookableOptionLists[idx].hide_for_clients) {
          return ['*', ...list];
        } else {
          return [null, '*', ...list];
        }
      } else {
        return list;
      }
    });
  }
  const selectedBookableIds = bookableOptions.map((list, idx) => bookableIds[idx] || list[0]).map((id, idx) => bookableOptions[idx].includes(id) ? id : bookableOptions[idx][0]);
  if (selectedBookableIds.some(id => id === undefined)) {
    return EMPTY_ARRAY;
  }
  return selectedBookableIds;
}
const ServiceView = () => {
  const $ = _c(291);
  const rrLocation = useLocation();
  const [locationId, setLocationId] = useLocationId();
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const dt = useCurrentDateTime(60000);
  const [bookableIds, setBookableIds] = useBookableIds();
  const [duration, setDuration] = useDuration();
  const {
    serviceId: serviceId_
  } = useParams();
  let serviceId = serviceId_;
  if (!isUUID(serviceId)) {
    if (serviceId.length > 1 && serviceId.length < 32) {
      let t0;
      if ($[0] !== serviceId) {
        t0 = toBase16(serviceId);
        $[0] = serviceId;
        $[1] = t0;
      } else {
        t0 = $[1];
      }
      serviceId = t0;
    } else {
      if (serviceId.length !== 32) {
        serviceId = null;
      }
    }
  }
  const serviceId_0 = serviceId;
  const navigate = useNavigate();
  const [localDate, setLocalDate] = React.useState("");
  const businessId = useAppSelector(_temp);
  let t0;
  if ($[2] !== serviceId_0) {
    t0 = state_0 => state_0.services.data[serviceId_0] || EMPTY_OBJECT;
    $[2] = serviceId_0;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const service = useAppSelector(t0);
  const business = useAppSelector(_temp2);
  const locationData = useAppSelector(_temp3);
  const serviceData = useAppSelector(_temp4);
  let t1;
  if ($[4] !== businessId || $[5] !== locationData || $[6] !== serviceData || $[7] !== serviceId_0) {
    t1 = Object.values(locationData).filter(_temp5).filter(l_0 => l_0.business_id === businessId).filter(l_1 => serviceData?.[serviceId_0]?.locations?.find(sl => sl.location_id === l_1?.id)).sort(_temp6);
    $[4] = businessId;
    $[5] = locationData;
    $[6] = serviceData;
    $[7] = serviceId_0;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const locations = t1;
  let t2;
  if ($[9] !== locationId || $[10] !== service.id) {
    t2 = state_4 => [locationId, service.id, state_4.businesses.status, state_4.locations.status, state_4.services.status, state_4.bookables.status, state_4.bookableGroups.status, state_4.bookableBusy.status, state_4.schedules.status].map(_temp7).includes("loading");
    $[9] = locationId;
    $[10] = service.id;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const isLoading = useAppSelector(t2);
  let t3;
  if ($[12] !== locationId) {
    t3 = state_5 => state_5.locations.data[locationId];
    $[12] = locationId;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const location = useAppSelector(t3);
  const bookableData = useAppSelector(_temp8);
  const bookableGroupData = useAppSelector(_temp9);
  const bookableBusyData = useAppSelector(_temp0);
  const scheduleData = useAppSelector(_temp1);
  const language = useAppSelector(_temp10);
  const geoipDetectedTimezone = useAppSelector(_temp11);
  let t4;
  if ($[14] !== locationId || $[15] !== service.locations) {
    t4 = service?.locations?.find(sl_0 => sl_0.location_id === locationId);
    $[14] = locationId;
    $[15] = service.locations;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const serviceLocation = t4;
  const serviceDurations = serviceLocation?.service_durations || EMPTY_ARRAY;
  let t5;
  if ($[17] !== serviceDurations) {
    t5 = serviceDurations.map(_temp12) || [];
    $[17] = serviceDurations;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const durations = t5;
  const bufferBefore = serviceLocation?.buffer_time_before || 0;
  const bufferAfter = serviceLocation?.buffer_time_after || 0;
  const slotInterval = serviceLocation?.slot_interval || 0;
  const startTimeScheduling = serviceLocation?.start_time_scheduling;
  const billableItemData = useAppSelector(_temp13);
  const invoiceSettings = useAppSelector(_temp14);
  let t6;
  if ($[19] !== billableItemData || $[20] !== invoiceSettings || $[21] !== locationId || $[22] !== serviceDurations) {
    let t7;
    if ($[24] !== billableItemData || $[25] !== invoiceSettings || $[26] !== locationId) {
      t7 = (agg, sd) => {
        if (sd.pricing_model === "hidden") {
          agg[sd.duration] = null;
          return agg;
        }
        const taxRate = invoiceSettings ? invoiceSettings.location_settings[invoiceSettings.is_location_specific ? locationId : "*"].default_tax_rate : 0;
        if (sd.pricing_model === "simple") {
          agg[sd.duration] = sd.price * (1 + taxRate / 100);
          return agg;
        }
        agg[sd.duration] = sd.billable_items.reduce((total, t8) => {
          const {
            billable_item_id,
            quantity
          } = t8;
          const item = billableItemData[billable_item_id];
          if (!item) {
            return total;
          }
          return total + (quantity || 0) * (item.unit_price[locationId] || 0);
        }, 0) * (1 + taxRate / 100);
        return agg;
      };
      $[24] = billableItemData;
      $[25] = invoiceSettings;
      $[26] = locationId;
      $[27] = t7;
    } else {
      t7 = $[27];
    }
    t6 = serviceDurations.reduce(t7, []);
    $[19] = billableItemData;
    $[20] = invoiceSettings;
    $[21] = locationId;
    $[22] = serviceDurations;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const serviceDurationPricing = t6;
  const price = serviceDurationPricing[duration];
  let t7;
  if ($[28] !== bookableData || $[29] !== bookableGroupData || $[30] !== locationId || $[31] !== serviceLocation) {
    t7 = serviceLocation && groupSubstitute(serviceLocation.requirements, locationId, bookableData, bookableGroupData);
    $[28] = bookableData;
    $[29] = bookableGroupData;
    $[30] = locationId;
    $[31] = serviceLocation;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  const requirements = t7;
  let t8;
  if ($[33] !== bookableData || $[34] !== requirements || $[35] !== serviceLocation) {
    t8 = serviceLocation && buildBookableOptionsList(requirements, bookableData) || EMPTY_ARRAY;
    $[33] = bookableData;
    $[34] = requirements;
    $[35] = serviceLocation;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  const requirementsList = t8;
  let t9;
  if ($[37] !== intl) {
    t9 = intl.formatMessage(messages.timeConjunction);
    $[37] = intl;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  const durationOptions = {
    units: ["d", "h", "m"],
    conjunction: ` ${t9} `,
    language,
    fallbacks: [language, "en"]
  };
  const locationTimezone = location?.timezone;
  let t10;
  if ($[39] !== geoipDetectedTimezone || $[40] !== locationTimezone || $[41] !== service.type) {
    t10 = service.type !== "on_premises" ? geoipDetectedTimezone || DateTime.local().zoneName || locationTimezone : locationTimezone;
    $[39] = geoipDetectedTimezone;
    $[40] = locationTimezone;
    $[41] = service.type;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  const defaultTimezone = t10;
  const [clientTimezone, setClientTimezone] = React.useState(defaultTimezone);
  let t11;
  if ($[43] !== business.settings.booking_window[0] || $[44] !== clientTimezone) {
    t11 = DateTime.utc().plus({
      minutes: business.settings.booking_window[0]
    }).setZone(clientTimezone).set({
      weekday: 1
    }).startOf("day");
    $[43] = business.settings.booking_window[0];
    $[44] = clientTimezone;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  const searchStartLocal = t11;
  let t12;
  if ($[46] !== business.settings.booking_window[1] || $[47] !== clientTimezone) {
    t12 = DateTime.utc().plus({
      minutes: business.settings.booking_window[1]
    }).setZone(clientTimezone).set({
      weekday: 7
    }).endOf("day");
    $[46] = business.settings.booking_window[1];
    $[47] = clientTimezone;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  const searchEndLocal = t12;
  let itree;
  if ($[49] !== bookableBusyData || $[50] !== bookableData || $[51] !== bookableGroupData || $[52] !== locationData || $[53] !== locationId || $[54] !== scheduleData || $[55] !== searchEndLocal || $[56] !== searchStartLocal || $[57] !== serviceData || $[58] !== serviceId_0) {
    const bookableIds_0 = getAssociatedBookableIds(locationId, serviceId_0, serviceData, bookableData, bookableGroupData);
    const timezone = locationData[locationId]?.timezone;
    const expandedRules = rulesToScheduleItems(Object.values(scheduleData), searchStartLocal.startOf("day").setZone("UTC").toISO().substring(0, 19) + "Z", searchEndLocal.endOf("day").setZone("UTC").toISO().substring(0, 19) + "Z", timezone, locationId, serviceId_0, bookableIds_0);
    itree = new IntervalTree();
    const slots = expandedRules;
    for (const ebs of Object.values(bookableBusyData)) {
      ebs.filter(eb => !eb.location_id || eb.location_id === locationId).forEach(eb_0 => {
        slots.push({
          start: eb_0.start,
          end: eb_0.end,
          type: "busy",
          bookableIds: [eb_0.bookable_id],
          notes: eb_0.description,
          booking_id: eb_0.booking_id
        });
      });
    }
    slots.forEach(s => itree.insert(s.start, s.end, s));
    $[49] = bookableBusyData;
    $[50] = bookableData;
    $[51] = bookableGroupData;
    $[52] = locationData;
    $[53] = locationId;
    $[54] = scheduleData;
    $[55] = searchEndLocal;
    $[56] = searchStartLocal;
    $[57] = serviceData;
    $[58] = serviceId_0;
    $[59] = itree;
  } else {
    itree = $[59];
  }
  const itree_0 = itree;
  let t13;
  let t14;
  if ($[60] !== defaultTimezone) {
    t13 = () => {
      setClientTimezone(defaultTimezone);
    };
    t14 = [defaultTimezone];
    $[60] = defaultTimezone;
    $[61] = t13;
    $[62] = t14;
  } else {
    t13 = $[61];
    t14 = $[62];
  }
  React.useEffect(t13, t14);
  let t15;
  if ($[63] !== navigate || $[64] !== requirementsList || $[65] !== rrLocation.search || $[66] !== startTimeScheduling) {
    t15 = (locationId_0, serviceId_1, selectedBookableIds, start, end, availability, timezone_0) => {
      let bookableIds_1;
      if (selectedBookableIds.includes("*")) {
        let availableIds = Object.entries(availability).filter(t16 => {
          const [, t17] = t16;
          const {
            available,
            busy
          } = t17;
          return startTimeScheduling ? !busy : available && !busy;
        }).map(_temp15);
        const randomRequirementsList = requirementsList.map(_temp16);
        bookableIds_1 = randomRequirementsList.map(options => {
          const availableId = options.find(o => availableIds.includes(o));
          availableIds = availableIds.filter(v_0 => v_0 != availableId);
          return availableId;
        });
      } else {
        bookableIds_1 = selectedBookableIds;
      }
      navigate(`/services/${serviceId_1}/book?` + queryString.stringify({
        ...queryString.parse(rrLocation.search),
        lid: locationId_0,
        bids: bookableIds_1,
        start,
        end,
        timezone: timezone_0
      }, {
        arrayFormat: "comma"
      }));
    };
    $[63] = navigate;
    $[64] = requirementsList;
    $[65] = rrLocation.search;
    $[66] = startTimeScheduling;
    $[67] = t15;
  } else {
    t15 = $[67];
  }
  const selectSlot = t15;
  let t16;
  let t17;
  if ($[68] !== duration || $[69] !== durations || $[70] !== setDuration) {
    t16 = () => {
      if (!duration && durations.length === 1) {
        setDuration(durations[0]);
      }
    };
    t17 = [duration, durations, setDuration];
    $[68] = duration;
    $[69] = durations;
    $[70] = setDuration;
    $[71] = t16;
    $[72] = t17;
  } else {
    t16 = $[71];
    t17 = $[72];
  }
  React.useEffect(t16, t17);
  let t18;
  let t19;
  if ($[73] !== dispatch || $[74] !== service.id || $[75] !== serviceId_0) {
    t18 = () => {
      if (service.id) {
        return;
      }
      dispatch(fetchService(serviceId_0));
    };
    t19 = [service.id, serviceId_0, dispatch];
    $[73] = dispatch;
    $[74] = service.id;
    $[75] = serviceId_0;
    $[76] = t18;
    $[77] = t19;
  } else {
    t18 = $[76];
    t19 = $[77];
  }
  React.useEffect(t18, t19);
  const includeAny = true;
  let t20;
  if ($[78] !== bookableData || $[79] !== requirements) {
    t20 = buildBookableDropdowns(requirements, true, bookableData);
    $[78] = bookableData;
    $[79] = requirements;
    $[80] = t20;
  } else {
    t20 = $[80];
  }
  const bookableOptionLists = t20;
  let t21;
  if ($[81] !== bookableIds || $[82] !== bookableOptionLists || $[83] !== setBookableIds) {
    t21 = () => {
      if (bookableOptionLists.length > 0 && bookableIds.length === 0) {
        setBookableIds(bookableOptionLists.map(_temp17));
      }
    };
    $[81] = bookableIds;
    $[82] = bookableOptionLists;
    $[83] = setBookableIds;
    $[84] = t21;
  } else {
    t21 = $[84];
  }
  let t22;
  if ($[85] !== bookableIds || $[86] !== bookableOptionLists || $[87] !== requirementsList || $[88] !== setBookableIds) {
    t22 = [requirementsList, bookableIds, bookableOptionLists, setBookableIds];
    $[85] = bookableIds;
    $[86] = bookableOptionLists;
    $[87] = requirementsList;
    $[88] = setBookableIds;
    $[89] = t22;
  } else {
    t22 = $[89];
  }
  React.useEffect(t21, t22);
  let t23;
  if ($[90] !== business.settings.booking_window[0] || $[91] !== clientTimezone || $[92] !== dt) {
    t23 = dt.plus({
      minutes: business.settings.booking_window[0]
    }).setZone(clientTimezone).startOf("day").setZone("UTC");
    $[90] = business.settings.booking_window[0];
    $[91] = clientTimezone;
    $[92] = dt;
    $[93] = t23;
  } else {
    t23 = $[93];
  }
  let t24;
  if ($[94] !== business.settings.booking_window[1] || $[95] !== dt) {
    t24 = dt.plus({
      minutes: business.settings.booking_window[1]
    });
    $[94] = business.settings.booking_window[1];
    $[95] = dt;
    $[96] = t24;
  } else {
    t24 = $[96];
  }
  let t25;
  if ($[97] !== t23 || $[98] !== t24) {
    t25 = [t23, t24];
    $[97] = t23;
    $[98] = t24;
    $[99] = t25;
  } else {
    t25 = $[99];
  }
  const dayStartBookingWindow = t25;
  let t26;
  if ($[100] !== business.settings.booking_window || $[101] !== dt) {
    let t27;
    if ($[103] !== dt) {
      t27 = v_1 => dt.plus({
        minutes: v_1
      });
      $[103] = dt;
      $[104] = t27;
    } else {
      t27 = $[104];
    }
    t26 = business.settings.booking_window.map(t27);
    $[100] = business.settings.booking_window;
    $[101] = dt;
    $[102] = t26;
  } else {
    t26 = $[102];
  }
  const bookingWindow = t26;
  let t27;
  if ($[105] !== bookableIds || $[106] !== bookingWindow || $[107] !== bufferAfter || $[108] !== bufferBefore || $[109] !== dayStartBookingWindow || $[110] !== duration || $[111] !== durations || $[112] !== isLoading || $[113] !== itree_0 || $[114] !== localDate || $[115] !== locationTimezone || $[116] !== requirementsList || $[117] !== searchEndLocal || $[118] !== searchStartLocal || $[119] !== serviceId_0 || $[120] !== slotInterval || $[121] !== startTimeScheduling) {
    t27 = () => {
      if (localDate || isLoading || bookableIds.filter(Boolean).length === 0 || !durations.includes(duration)) {
        return;
      }
      for (let dt_0 = searchStartLocal.startOf("day"); dt_0 < searchEndLocal; dt_0 = dt_0.plus({
        days: 7
      })) {
        const startUTC = dt_0.setZone("UTC");
        const endUTC = dt_0.plus({
          days: 7
        }).setZone("UTC");
        const slots_0 = getSlotsPublic({
          itree: itree_0,
          startUTC,
          endUTC,
          duration,
          bookingWindow: dayStartBookingWindow,
          requiredResources: bookableIds,
          bookableOptions: requirementsList,
          serviceId: serviceId_0,
          bufferBefore,
          bufferAfter,
          slotInterval,
          startTimeScheduling,
          locationTimezone
        }).filter(slot => bookingWindow[0].toISO() < slot.start && slot.end < bookingWindow[1].toISO());
        if (slots_0.length) {
          const date = dt_0.toISO().substring(0, 19);
          setLocalDate(date);
          return;
        }
      }
      setLocalDate(searchStartLocal.toISO().substring(0, 19));
    };
    $[105] = bookableIds;
    $[106] = bookingWindow;
    $[107] = bufferAfter;
    $[108] = bufferBefore;
    $[109] = dayStartBookingWindow;
    $[110] = duration;
    $[111] = durations;
    $[112] = isLoading;
    $[113] = itree_0;
    $[114] = localDate;
    $[115] = locationTimezone;
    $[116] = requirementsList;
    $[117] = searchEndLocal;
    $[118] = searchStartLocal;
    $[119] = serviceId_0;
    $[120] = slotInterval;
    $[121] = startTimeScheduling;
    $[122] = t27;
  } else {
    t27 = $[122];
  }
  let t28;
  if ($[123] !== bookableBusyData || $[124] !== bookableData || $[125] !== bookableGroupData || $[126] !== bookableIds || $[127] !== bookingWindow || $[128] !== bufferAfter || $[129] !== bufferBefore || $[130] !== business || $[131] !== clientTimezone || $[132] !== dayStartBookingWindow || $[133] !== duration || $[134] !== durations || $[135] !== isLoading || $[136] !== itree_0 || $[137] !== localDate || $[138] !== locationData || $[139] !== locationId || $[140] !== locationTimezone || $[141] !== requirementsList || $[142] !== scheduleData || $[143] !== searchEndLocal || $[144] !== searchStartLocal || $[145] !== serviceData || $[146] !== serviceId_0 || $[147] !== slotInterval || $[148] !== startTimeScheduling) {
    t28 = [localDate, isLoading, duration, durations, bookableIds, business, locationId, serviceId_0, locationData, serviceData, bookableData, bookableGroupData, scheduleData, bookableBusyData, bufferAfter, bufferBefore, slotInterval, requirementsList, clientTimezone, searchStartLocal, searchEndLocal, itree_0, startTimeScheduling, locationTimezone, dayStartBookingWindow, bookingWindow];
    $[123] = bookableBusyData;
    $[124] = bookableData;
    $[125] = bookableGroupData;
    $[126] = bookableIds;
    $[127] = bookingWindow;
    $[128] = bufferAfter;
    $[129] = bufferBefore;
    $[130] = business;
    $[131] = clientTimezone;
    $[132] = dayStartBookingWindow;
    $[133] = duration;
    $[134] = durations;
    $[135] = isLoading;
    $[136] = itree_0;
    $[137] = localDate;
    $[138] = locationData;
    $[139] = locationId;
    $[140] = locationTimezone;
    $[141] = requirementsList;
    $[142] = scheduleData;
    $[143] = searchEndLocal;
    $[144] = searchStartLocal;
    $[145] = serviceData;
    $[146] = serviceId_0;
    $[147] = slotInterval;
    $[148] = startTimeScheduling;
    $[149] = t28;
  } else {
    t28 = $[149];
  }
  React.useEffect(t27, t28);
  let t29;
  let t30;
  if ($[150] !== bookableData || $[151] !== bookableGroupData || $[152] !== bookableIds || $[153] !== isLoading || $[154] !== locationId || $[155] !== serviceData || $[156] !== serviceId_0 || $[157] !== setBookableIds) {
    t29 = () => {
      if (!isLoading) {
        const validBookableIds = getValidSelection(locationId, serviceId_0, bookableIds, true, serviceData, bookableData, bookableGroupData);
        if (bookableIds.length !== validBookableIds.length || !bookableIds.every((id_1, idx) => id_1 === validBookableIds[idx])) {
          return setBookableIds(validBookableIds);
        }
      }
    };
    t30 = [bookableData, bookableGroupData, bookableIds, isLoading, locationId, serviceData, serviceId_0, setBookableIds];
    $[150] = bookableData;
    $[151] = bookableGroupData;
    $[152] = bookableIds;
    $[153] = isLoading;
    $[154] = locationId;
    $[155] = serviceData;
    $[156] = serviceId_0;
    $[157] = setBookableIds;
    $[158] = t29;
    $[159] = t30;
  } else {
    t29 = $[158];
    t30 = $[159];
  }
  React.useEffect(t29, t30);
  const [openBookableId, setOpenBookableId] = React.useState(null);
  const openBookable = bookableData[openBookableId];
  let t31;
  bb0: {
    if (!service) {
      t31 = false;
      break bb0;
    }
    const isRequirementsListValid = _temp18;
    const serviceLocation_0 = service.locations.find(sl_1 => sl_1.location_id === locationId);
    if (!serviceLocation_0) {
      t31 = false;
      break bb0;
    }
    const requirements_0 = groupSubstitute(serviceLocation_0.requirements, locationId, bookableData, bookableGroupData);
    const requirementsList_1 = buildBookableOptionsList(requirements_0, bookableData);
    t31 = isRequirementsListValid(requirementsList_1);
  }
  const isServiceRequirementsValid = t31;
  if (!locationId) {
    let t32;
    if ($[160] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = <LocationSelect />;
      $[160] = t32;
    } else {
      t32 = $[160];
    }
    return t32;
  }
  if (!isServiceRequirementsValid) {
    const t32 = "/" + (locationId ? `?lid=${locationId}` : "");
    let t33;
    if ($[161] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = <FormattedMessage id="Back" defaultMessage="Back" />;
      $[161] = t33;
    } else {
      t33 = $[161];
    }
    let t34;
    if ($[162] !== t32) {
      t34 = <EmptyState heading="Service is not currently available" subheading="Please contact us for more information." callToAction={<Button as={ReachLink} color="gray" variant="outlined" to={t32}>{t33}</Button>} />;
      $[162] = t32;
      $[163] = t34;
    } else {
      t34 = $[163];
    }
    return t34;
  }
  if (requirementsList.length === 0) {
    const t32 = "/" + (locationId ? `?lid=${locationId}` : "");
    let t33;
    if ($[164] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = <FormattedMessage id="Back" defaultMessage="Back" />;
      $[164] = t33;
    } else {
      t33 = $[164];
    }
    let t34;
    if ($[165] !== t32) {
      t34 = <EmptyState heading="Service is not currently available" subheading="Please contact us for more information." callToAction={<Button as={ReachLink} color="gray" variant="outlined" to={t32}>{t33}</Button>} />;
      $[165] = t32;
      $[166] = t34;
    } else {
      t34 = $[166];
    }
    return t34;
  }
  if (business.is_deleted || business.subscription_status === "cancelled" || !business.online) {
    let t32;
    if ($[167] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = <BusinessOfflineState />;
      $[167] = t32;
    } else {
      t32 = $[167];
    }
    return t32;
  }
  if (isLoading) {
    let t32;
    if ($[168] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = <Loader />;
      $[168] = t32;
    } else {
      t32 = $[168];
    }
    return t32;
  }
  if (bookableIds.some(_temp19)) {
    let t32;
    if ($[169] !== bookableIds) {
      t32 = bookableIds.indexOf(null);
      $[169] = bookableIds;
      $[170] = t32;
    } else {
      t32 = $[170];
    }
    const idx_0 = t32;
    const options_0 = bookableOptionLists[idx_0];
    const label = options_0.label;
    let T0;
    let T1;
    let bookables;
    let select;
    let t33;
    let t34;
    let t35;
    let t36;
    let t37;
    let t38;
    let t39;
    if ($[171] !== bookableData || $[172] !== bookableIds || $[173] !== idx_0 || $[174] !== label || $[175] !== locationId || $[176] !== options_0.bookables || $[177] !== setBookableIds) {
      bookables = options_0.bookables.filter(_temp20);
      let t40;
      if ($[189] !== bookableIds || $[190] !== idx_0 || $[191] !== setBookableIds) {
        t40 = bid => {
          setOpenBookableId(null);
          setBookableIds(bookableIds.map((v_2, idIdx) => idx_0 === idIdx ? bid : v_2));
        };
        $[189] = bookableIds;
        $[190] = idx_0;
        $[191] = setBookableIds;
        $[192] = t40;
      } else {
        t40 = $[192];
      }
      select = t40;
      T1 = Grid;
      t38 = 8;
      const t41 = "/" + (locationId ? `?lid=${locationId}` : "");
      let t42;
      if ($[193] !== label) {
        t42 = <FormattedMessage id="Public.BookView.selectServicePrompt" defaultMessage={"Select \"{label}\""} values={{
          label
        }} />;
        $[193] = label;
        $[194] = t42;
      } else {
        t42 = $[194];
      }
      if ($[195] !== t41 || $[196] !== t42) {
        t39 = <BookStepHeading backTo={t41}>{t42}</BookStepHeading>;
        $[195] = t41;
        $[196] = t42;
        $[197] = t39;
      } else {
        t39 = $[197];
      }
      T0 = Grid;
      if ($[198] === Symbol.for("react.memo_cache_sentinel")) {
        t33 = ["repeat( auto-fit, minmax(150px, 1fr) )"];
        $[198] = t33;
      } else {
        t33 = $[198];
      }
      t34 = "center";
      t35 = 8;
      t36 = 8;
      let t43;
      if ($[199] !== bookableData || $[200] !== select) {
        t43 = t44 => {
          const {
            name,
            id: bid_0
          } = t44;
          return <Grid onClick={() => select(bid_0)} onKeyPress={e => e.key === "Enter" && select(bid_0)} tabIndex={0} role="button" key={bid_0} sx={{
            cursor: "pointer"
          }} justifyItems="center"><Avatar size={100} image={bookableData[bid_0]?.image_id ? getCdnImageUrlFromId(bookableData[bid_0].image_id) : null} icon={faUser} name={name} /><Grid gridGap={2}><Heading fontWeight={500} fontSize={3} textAlign="center">{name}</Heading>{bookableData[bid_0]?.description && <Flex justifyContent="center"><Button onClick={e_0 => {
                  e_0.stopPropagation();
                  setOpenBookableId(bid_0);
                }} as="button" type="button" color="primary" variant="outlined" size="small"><FormattedMessage id="About" defaultMessage="About" /></Button></Flex>}</Grid></Grid>;
        };
        $[199] = bookableData;
        $[200] = select;
        $[201] = t43;
      } else {
        t43 = $[201];
      }
      t37 = bookables.map(t43);
      $[171] = bookableData;
      $[172] = bookableIds;
      $[173] = idx_0;
      $[174] = label;
      $[175] = locationId;
      $[176] = options_0.bookables;
      $[177] = setBookableIds;
      $[178] = T0;
      $[179] = T1;
      $[180] = bookables;
      $[181] = select;
      $[182] = t33;
      $[183] = t34;
      $[184] = t35;
      $[185] = t36;
      $[186] = t37;
      $[187] = t38;
      $[188] = t39;
    } else {
      T0 = $[178];
      T1 = $[179];
      bookables = $[180];
      select = $[181];
      t33 = $[182];
      t34 = $[183];
      t35 = $[184];
      t36 = $[185];
      t37 = $[186];
      t38 = $[187];
      t39 = $[188];
    }
    let t40;
    if ($[202] !== T0 || $[203] !== t33 || $[204] !== t34 || $[205] !== t35 || $[206] !== t36 || $[207] !== t37) {
      t40 = <T0 gridTemplateColumns={t33} justifyItems={t34} p={t35} gridGap={t36}>{t37}</T0>;
      $[202] = T0;
      $[203] = t33;
      $[204] = t34;
      $[205] = t35;
      $[206] = t36;
      $[207] = t37;
      $[208] = t40;
    } else {
      t40 = $[208];
    }
    let t41;
    if ($[209] !== bookables.length || $[210] !== intl || $[211] !== select) {
      t41 = bookables.length > 1 && <Flex justifyContent="flex-end"><Button variant="outlined" onClick={() => select("*")}>{intl.formatMessage(messages["No preference"])}</Button></Flex>;
      $[209] = bookables.length;
      $[210] = intl;
      $[211] = select;
      $[212] = t41;
    } else {
      t41 = $[212];
    }
    const t42 = !!openBookableId;
    let t43;
    if ($[213] === Symbol.for("react.memo_cache_sentinel")) {
      t43 = () => setOpenBookableId(null);
      $[213] = t43;
    } else {
      t43 = $[213];
    }
    const t44 = openBookable || EMPTY_OBJECT;
    let t45;
    if ($[214] !== t44) {
      t45 = {
        openBookable: t44
      };
      $[214] = t44;
      $[215] = t45;
    } else {
      t45 = $[215];
    }
    let t46;
    if ($[216] !== openBookableId || $[217] !== select) {
      t46 = t47 => {
        const {
          openBookable: openBookable_0,
          close
        } = t47;
        return <Grid borderRadius={2} p={4} gridGap={8}><Box sx={{
            position: "absolute",
            top: 0,
            right: 0
          }} p={4}><CloseButton isDark={true} onClick={close} /></Box><Grid><Flex justifyContent="center"><Avatar size={150} image={openBookable_0.image_id ? getCdnImageUrlFromId(openBookable_0.image_id) : null} name={openBookable_0.name} /></Flex><Flex justifyContent="center"><Box sx={{
                overflow: "hidden",
                display: "-webkit-box",
                overflowWrap: "break-word",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical"
              }}><Text fontSize={3} fontFamily="heading">{openBookable_0.name}</Text></Box></Flex></Grid><Grid>{openBookable_0.description.split("\n").map(_temp21)}</Grid><Flex justifyContent="flex-end"><Button variant="flat" mr={2} onClick={close}>Close</Button><Button onClick={() => select(openBookableId)}>Select</Button></Flex></Grid>;
      };
      $[216] = openBookableId;
      $[217] = select;
      $[218] = t46;
    } else {
      t46 = $[218];
    }
    let t47;
    if ($[219] !== t42 || $[220] !== t45 || $[221] !== t46) {
      t47 = <Modal isOpen={t42} close={t43} size="medium" props={t45} autoFocus={false}>{t46}</Modal>;
      $[219] = t42;
      $[220] = t45;
      $[221] = t46;
      $[222] = t47;
    } else {
      t47 = $[222];
    }
    let t48;
    if ($[223] !== T1 || $[224] !== t38 || $[225] !== t39 || $[226] !== t40 || $[227] !== t41 || $[228] !== t47) {
      t48 = <T1 gridGap={t38}>{t39}{t40}{t41}{t47}</T1>;
      $[223] = T1;
      $[224] = t38;
      $[225] = t39;
      $[226] = t40;
      $[227] = t41;
      $[228] = t47;
      $[229] = t48;
    } else {
      t48 = $[229];
    }
    return t48;
  }
  if (!duration) {
    const t32 = "/" + (locationId ? `?lid=${locationId}` : "");
    let t33;
    if ($[230] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = <FormattedMessage id="selectServiceDurationPublic" defaultMessage="Select duration" />;
      $[230] = t33;
    } else {
      t33 = $[230];
    }
    let t34;
    if ($[231] !== t32) {
      t34 = <BookStepHeading backTo={t32}>{t33}</BookStepHeading>;
      $[231] = t32;
      $[232] = t34;
    } else {
      t34 = $[232];
    }
    let t35;
    if ($[233] === Symbol.for("react.memo_cache_sentinel")) {
      t35 = ["repeat( auto-fit, minmax(150px, 1fr) )"];
      $[233] = t35;
    } else {
      t35 = $[233];
    }
    return <Grid gridGap={8}>{t34}<Grid gridTemplateColumns={t35} justifyItems="center" p={8}>{durations.sort(_temp22).map((duration_0, idx_2) => <Grid onClick={() => setDuration(duration_0)} onKeyPress={e_1 => e_1.key === "Enter" && setDuration(duration_0)} tabIndex={0} role="button" key={idx_2} sx={{
          cursor: "pointer"
        }} justifyItems="center"><Avatar size={100} icon={faClock} name={humanizeDuration(duration_0 * 1000 * 60, durationOptions)} /><Grid gridGap={2}><Heading fontWeight={500} fontSize={3} textAlign="center">{humanizeDuration(duration_0 * 1000 * 60, durationOptions)}</Heading></Grid></Grid>)}</Grid></Grid>;
  }
  if (bookableIds.filter(Boolean).length === 0 || !localDate) {
    let t32;
    if ($[234] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = <Loader />;
      $[234] = t32;
    } else {
      t32 = $[234];
    }
    return t32;
  }
  const startUTC_0 = DateTime.fromISO(localDate, {
    zone: clientTimezone
  }).setZone("UTC");
  const endUTC_0 = DateTime.fromISO(localDate, {
    zone: clientTimezone
  }).plus({
    days: 7
  }).setZone("UTC");
  let t32;
  if ($[235] !== bookingWindow) {
    t32 = slot_0 => bookingWindow[0].toISO() < slot_0.start && slot_0.end < bookingWindow[1].toISO();
    $[235] = bookingWindow;
    $[236] = t32;
  } else {
    t32 = $[236];
  }
  const slots_1 = getSlotsPublic({
    itree: itree_0,
    startUTC: startUTC_0,
    endUTC: endUTC_0,
    duration,
    bookingWindow: dayStartBookingWindow,
    requiredResources: bookableIds,
    bookableOptions: requirementsList,
    serviceId: serviceId_0,
    bufferBefore,
    bufferAfter,
    slotInterval,
    startTimeScheduling,
    locationTimezone
  }).filter(t32);
  const maxSlots = business.settings.max_slots_per_day;
  const minSlots = business.settings.min_slots_per_day;
  let t33;
  if ($[237] !== maxSlots || $[238] !== minSlots) {
    t33 = v_5 => {
      let slots_2 = v_5.slots;
      if (maxSlots) {
        const rng = seedrandom(v_5.date.valueOf());
        const count = Math.floor(rng() * (maxSlots - minSlots + 1)) + minSlots;
        slots_2 = shuffleSeed.shuffle(v_5.slots, v_5.date.valueOf()).filter((_, idx_3) => idx_3 < count).sort(_temp23);
      }
      return {
        ...v_5,
        slots: slots_2
      };
    };
    $[237] = maxSlots;
    $[238] = minSlots;
    $[239] = t33;
  } else {
    t33 = $[239];
  }
  const days = slotsToPages(slots_1, startUTC_0, endUTC_0, clientTimezone, locationId, bookableIds).map(t33);
  let T0;
  let t34;
  let t35;
  let t36;
  if ($[240] !== bookableData || $[241] !== bookableIds || $[242] !== bookableOptionLists || $[243] !== durations.length || $[244] !== intl || $[245] !== language || $[246] !== locationData || $[247] !== locationId || $[248] !== locations || $[249] !== service.name || $[250] !== setBookableIds || $[251] !== setLocationId) {
    const filteredBookableOptionLists = bookableOptionLists.map(_temp24).filter(_temp25).filter(_temp26);
    T0 = Grid;
    t34 = 4;
    const t37 = "/" + (locationId ? `?lid=${locationId}` : "");
    let t38;
    if ($[256] !== language || $[257] !== service.name) {
      t38 = getTranslation(service.name, language);
      $[256] = language;
      $[257] = service.name;
      $[258] = t38;
    } else {
      t38 = $[258];
    }
    if ($[259] !== t37 || $[260] !== t38) {
      t35 = <BookStepHeading backTo={t37}>{t38}</BookStepHeading>;
      $[259] = t37;
      $[260] = t38;
      $[261] = t35;
    } else {
      t35 = $[261];
    }
    t36 = (locations.length > 0 || filteredBookableOptionLists.length > 0 || durations.length > 0) && <Row>{locations.length > 1 && <Column><DropdownSelect placement="bottom-start" onChange={v_6 => setLocationId(v_6)} value={locationId || "*"} itemToString={v_7 => locationData[v_7] ? locationData[v_7].name : ""} items={locations.map(_temp27)} renderToggle={_temp28} /></Column>}{filteredBookableOptionLists.map(boption_3 => <Column key={boption_3.index}><DropdownSelect placement="bottom-start" onChange={v_8 => {
          setBookableIds(bookableIds.map((id_3, idx_) => boption_3.index === idx_ ? v_8 : id_3));
        }} value={bookableIds[boption_3.index] || "*"} itemToString={v_9 => bookableData[bookableIds[boption_3.index]] ? bookableData[bookableIds[boption_3.index]].name : intl.formatMessage(messages["No preference"])} items={boption_3.bookables.map(b_2 => ({
          label: b_2.id !== "*" ? b_2.name : intl.formatMessage(messages["No preference"]),
          value: b_2.id,
          iconElement: <Avatar name={bookableData[b_2.id]?.name} icon={bookableData[b_2.id]?.staff_member_id ? faUser : faCube} image={bookableData[b_2.id]?.image_id ? getCdnImageUrlFromId(bookableData[b_2.id].image_id) : null} size={30} />
        }))} renderToggle={(props_0, t39) => {
          const {
            isOpen: isOpen_0,
            displayString: displayString_0,
            items: items_0
          } = t39;
          return <Link {...props_0} as="button" type="button" variant="discreetLink" textAlign="left" display="flex" alignItems="center"><Tippy content={boption_3.label && <>{boption_3.label == "Resource" ? <FormattedMessage id="Resource" defaultMessage="Resource" /> : boption_3.label}</>}><Avatar name={bookableData[bookableIds[boption_3.index]]?.name} icon={bookableData[bookableIds[boption_3.index]]?.staff_member_id ? faUser : faCube} image={bookableData[bookableIds[boption_3.index]]?.image_id ? getCdnImageUrlFromId(bookableData[bookableIds[boption_3.index]].image_id) : null} size={30} mr={2} /></Tippy><Text>{displayString_0}</Text>{!(items_0.length === 1 && displayString_0) && <Text ml={2}><FontAwesomeIcon icon={isOpen_0 ? faChevronUp : faChevronDown} /></Text>}</Link>;
        }} /></Column>)}</Row>;
    $[240] = bookableData;
    $[241] = bookableIds;
    $[242] = bookableOptionLists;
    $[243] = durations.length;
    $[244] = intl;
    $[245] = language;
    $[246] = locationData;
    $[247] = locationId;
    $[248] = locations;
    $[249] = service.name;
    $[250] = setBookableIds;
    $[251] = setLocationId;
    $[252] = T0;
    $[253] = t34;
    $[254] = t35;
    $[255] = t36;
  } else {
    T0 = $[252];
    t34 = $[253];
    t35 = $[254];
    t36 = $[255];
  }
  const t37 = durations.length > 1 && <Flex alignItems="center"><DropdownSelect placement="bottom-start" name="duration-select" onChange={v_10 => setDuration(v_10)} value={duration || null} itemToString={v_11 => humanizeDuration(v_11 * 1000 * 60, durationOptions)} items={durations.sort(_temp29).map(v_12 => ({
      label: humanizeDuration(v_12 * 1000 * 60, durationOptions),
      value: v_12,
      iconElement: <IconWrapper icon={faStopwatch} size={1} />
    }))} renderToggle={_temp30} /></Flex>;
  const t38 = serviceDurations.length === 1 && <Flex alignItems="center"><Tippy content={<FormattedMessage id="Duration" defaultMessage="Duration" />}><IconWrapper icon={faStopwatch} size={1} mr={2} /></Tippy>{" "}<Text>{humanizeDuration(duration * 1000 * 60, durationOptions)}</Text></Flex>;
  let t39;
  if ($[262] !== locationData || $[263] !== locationId || $[264] !== price || $[265] !== service.show_pricing) {
    t39 = service.show_pricing && price !== null && <Flex alignItems="center"><Tippy content={<FormattedMessage id="Price" defaultMessage="Price" />}><IconWrapper icon={faTags} size={1} mr={2} /></Tippy>{" "}{price > 0 ? <Text suppressHydrationWarning={true}>{currencyFormat(price, locationData[locationId].currency)}</Text> : <Text>No charge</Text>}</Flex>;
    $[262] = locationData;
    $[263] = locationId;
    $[264] = price;
    $[265] = service.show_pricing;
    $[266] = t39;
  } else {
    t39 = $[266];
  }
  let t40;
  if ($[267] !== clientTimezone || $[268] !== service.type) {
    t40 = service.type !== "on_premises" && <Box><DropdownSelect placement="bottom-start" width={["100%", "auto"]} flexGrow={[1, "initial"]} onChange={setClientTimezone} value={clientTimezone} itemToString={_temp31} items={timezones} renderToggle={_temp32} /></Box>;
    $[267] = clientTimezone;
    $[268] = service.type;
    $[269] = t40;
  } else {
    t40 = $[269];
  }
  let t41;
  if ($[270] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <IconWrapper icon={faInfo} size={1} mr={2} color="success" />;
    $[270] = t41;
  } else {
    t41 = $[270];
  }
  let t42;
  if ($[271] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <Flex alignItems="center">{t41}<CroppedText fontFamily="body"><FormattedMessage id="Public.ServiceView.selectSlotBelow" defaultMessage="Select an available slot below" /></CroppedText></Flex>;
    $[271] = t42;
  } else {
    t42 = $[271];
  }
  let t43;
  if ($[272] !== clientTimezone || $[273] !== language || $[274] !== localDate) {
    t43 = <Flex justifyContent="center"><CalendarPicker variant="flat" color="text" mode="week" value={localDate} language={language} onChange={setLocalDate} timezone={clientTimezone} /></Flex>;
    $[272] = clientTimezone;
    $[273] = language;
    $[274] = localDate;
    $[275] = t43;
  } else {
    t43 = $[275];
  }
  const T1 = Box;
  const t44 = "300px";
  const t45 = isLoading ? <><Loader isContained={true} /><Box height="10rem" /></> : <>{slots_1.length === 0 ? <EmptyState heading={<FormattedMessage id="Public.ServiceView.noOpenings" defaultMessage="No openings available for this week" />} subheading={<FormattedMessage id="Public.ServiceView.noOpeningsSubtitle" defaultMessage="Please try another" />} /> : <Box width="100%" overflowX="auto"><Box><Flex mb={2} minWidth="100%">{days.map((day, idx_4) => <Flex key={idx_4} width="14.285714285714286%" justifyContent="center" flexDirection={["column", "row"]} flexGrow={1} fontWeight={400} textAlign="center" backgroundColor="none" sx={{
            whiteSpace: "nowrap"
          }}><Text mr={[0, 1]}>{day.date.setLocale(language).toFormat("ccc")}</Text><Text>{day.date.setLocale(language).toFormat("d")}</Text></Flex>)}</Flex><Flex minWidth="100%">{days.map((day_0, idx_5) => <Box key={idx_5} flexGrow={1} width="14.285714285714286%" p="1px" sx={{
            verticalAlign: "top"
          }}>{day_0.slots.map((slot_1, idx_6) => <Button variant="solid" color="gray.0" size="small" key={idx_6} mb={1} py={2} width="100%" fontSize={[0, 2]} minHeight={["36px", "unset"]} sx={{
              paddingLeft: "6px!important",
              paddingRight: "6px!important"
            }} onClick={() => selectSlot(slot_1.locationId, serviceId_0, slot_1.bookableIds, slot_1.start, slot_1.end, slot_1.availability, service.type !== "on_premises" ? clientTimezone : undefined)}><Box>{DateTime.fromISO(slot_1.start, {
                  zone: clientTimezone
                }).toFormat("t").split(" ").map(_temp33)}</Box></Button>)}</Box>)}</Flex></Box></Box>}</>;
  let t46;
  if ($[276] !== T1 || $[277] !== t45) {
    t46 = <T1 minHeight={t44}>{t45}</T1>;
    $[276] = T1;
    $[277] = t45;
    $[278] = t46;
  } else {
    t46 = $[278];
  }
  let t47;
  if ($[279] !== T0 || $[280] !== t34 || $[281] !== t35 || $[282] !== t36 || $[283] !== t37 || $[284] !== t38 || $[285] !== t39 || $[286] !== t40 || $[287] !== t42 || $[288] !== t43 || $[289] !== t46) {
    t47 = <><T0 gridGap={t34}>{t35}{t36}{t37}{t38}{t39}{t40}{t42}{t43}{t46}</T0></>;
    $[279] = T0;
    $[280] = t34;
    $[281] = t35;
    $[282] = t36;
    $[283] = t37;
    $[284] = t38;
    $[285] = t39;
    $[286] = t40;
    $[287] = t42;
    $[288] = t43;
    $[289] = t46;
    $[290] = t47;
  } else {
    t47 = $[290];
  }
  return t47;
};
export default ServiceView;
function _temp(state) {
  return state.public.businessId;
}
function _temp2(state_1) {
  return state_1.businesses.data[state_1.public.businessId];
}
function _temp3(state_2) {
  return state_2.locations.data;
}
function _temp4(state_3) {
  return state_3.services.data;
}
function _temp5(l) {
  return !l.is_deleted;
}
function _temp6(a, b) {
  return a.name < b.name ? -1 : 1;
}
function _temp7(v) {
  return v || "loading";
}
function _temp8(state_6) {
  return state_6.bookables.data;
}
function _temp9(state_7) {
  return state_7.bookableGroups.data;
}
function _temp0(state_8) {
  return state_8.bookableBusy.data;
}
function _temp1(state_9) {
  return state_9.schedules.data;
}
function _temp10(state_10) {
  return state_10.userPreferences.language;
}
function _temp11(state_11) {
  try {
    DateTime.utc().setZone(state_11.geoip.time_zone);
    return state_11.geoip.time_zone;
  } catch {
    return null;
  }
}
function _temp12(d) {
  return d.duration;
}
function _temp13(state_12) {
  return state_12.billableItems.data;
}
function _temp14(state_13) {
  return state_13.invoiceSettings.data[state_13.public.businessId];
}
function _temp15(t0) {
  const [id_0] = t0;
  return id_0;
}
function _temp16(ids) {
  return shuffleSeed.shuffle(ids, new Date().valueOf());
}
function _temp17(boption) {
  return boption.hide_for_clients ? "*" : null;
}
function _temp18(requirementsList_0) {
  if (requirementsList_0.length == 0) {
    return false;
  }
  for (const requirement of requirementsList_0) {
    if (requirement.length == 0) {
      return false;
    }
  }
  return true;
}
function _temp19(v_4) {
  return v_4 === null;
}
function _temp20(t0) {
  const {
    id: id_2
  } = t0;
  return id_2 !== "*";
}
function _temp21(v_3, idx_1) {
  return <CroppedText key={idx_1} data-sentry-element="CroppedText" data-sentry-component="_temp21" data-sentry-source-file="component.tsx">{v_3}</CroppedText>;
}
function _temp22(a_0, b_0) {
  return a_0 < b_0 ? -1 : 1;
}
function _temp23(a_1, b_1) {
  return a_1.start < b_1.start ? -1 : 1;
}
function _temp24(boption_0, index) {
  return {
    ...boption_0,
    index
  };
}
function _temp25(boption_1) {
  return !boption_1.hide_for_clients;
}
function _temp26(boption_2) {
  return boption_2.bookables.length > 1 || boption_2.bookables[0]?.id !== 0;
}
function _temp27(l_2) {
  return {
    label: l_2.name,
    value: l_2.id,
    iconElement: <IconWrapper icon={faMapMarker} size={1} />
  };
}
function _temp28(props, t0) {
  const {
    isOpen,
    displayString,
    items
  } = t0;
  return <Link {...props} as="button" type="button" variant="discreetLink" textAlign="left" display="flex" alignItems="center" fontSize={2} data-sentry-element="Link" data-sentry-component="_temp28" data-sentry-source-file="component.tsx"><Tippy content={<FormattedMessage id="Location" defaultMessage="Location" />} data-sentry-element="Tippy" data-sentry-source-file="component.tsx"><IconWrapper icon={faMapMarker} size={1} mr={2} data-sentry-element="IconWrapper" data-sentry-source-file="component.tsx" /></Tippy>{displayString}{!(items.length === 1 && displayString) && <Text ml={2}><FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} /></Text>}</Link>;
}
function _temp29(a_2, b_3) {
  return a_2 < b_3 ? -1 : 1;
}
function _temp30(props_1, t0) {
  const {
    isOpen: isOpen_1,
    displayString: displayString_1,
    items: items_1
  } = t0;
  return <Link {...props_1} as="button" type="button" variant="discreetLink" textAlign="left" display="flex" alignItems="center" fontSize={2} data-sentry-element="Link" data-sentry-component="_temp30" data-sentry-source-file="component.tsx"><Tippy content={<FormattedMessage id="Duration" defaultMessage="Duration" />} data-sentry-element="Tippy" data-sentry-source-file="component.tsx"><IconWrapper icon={faStopwatch} size={1} mr={2} data-sentry-element="IconWrapper" data-sentry-source-file="component.tsx" /></Tippy>{displayString_1}{!(items_1.length === 1 && displayString_1) && <Text ml={2}><FontAwesomeIcon icon={isOpen_1 ? faChevronUp : faChevronDown} /></Text>}</Link>;
}
function _temp31(v_13) {
  return v_13?.replace("_", " ");
}
function _temp32(props_2, t0) {
  const {
    isOpen: isOpen_2,
    displayString: displayString_2,
    items: items_2
  } = t0;
  return <Link {...props_2} as="button" type="button" variant="discreetLink" textAlign="left" display="flex" justifyContent="center" fontSize={2} data-sentry-element="Link" data-sentry-component="_temp32" data-sentry-source-file="component.tsx"><Tippy content="Time zone" data-sentry-element="Tippy" data-sentry-source-file="component.tsx"><IconWrapper icon={faClock} size={1} mr={2} data-sentry-element="IconWrapper" data-sentry-source-file="component.tsx" /></Tippy>{displayString_2}{" "}{!(items_2.length === 1 && displayString_2) && <Box ml={2}><FontAwesomeIcon icon={isOpen_2 ? faChevronUp : faChevronDown} /></Box>}</Link>;
}
function _temp33(v_14, idx_7) {
  return <React.Fragment key={idx_7}>{idx_7 ? " " : ""}<Box sx={{
      display: ["block", "inline"]
    }} data-sentry-element="Box" data-sentry-source-file="component.tsx">{v_14}</Box></React.Fragment>;
}